import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Scene } from "@/mocks/scenes";
import { apiFetch, apiGet, apiSend, apiUpload, apiUrl } from "@/lib/api";
import { confirmDialog, toast } from "@/components/ui/feedback";

// Scene detail as a standalone page (exguide exhibit-detail pattern):
// breadcrumb → cover header → pill tabs → full-width card content.
// Replaces the former right-side drawer.

interface SceneImage { id: string; url: string; tags: string[]; description: string; analyzed: boolean; usageCount: number }
interface SceneKnowledge { id: string; title: string; content: string; source: string; type: string; purpose: string; isEnabled: boolean; vectorStatus: string; materialId: string }
interface SceneMaterial { id: string; fileName: string; fileType: string; parseStatus: string; parseError: string }
interface SceneAudio { script: string; voice: string; audioUrl: string; duration: string; status: string }
interface AudioTrack { id: string; voice: string; lang: string; audioUrl: string; durationSec: number; status: string; error: string; updatedAt: string }

const TRACK_VOICES = ["鲟侠船长", "金梅导游", "小船长"];
const TRACK_LANGS: [string, string][] = [
  ["zh", "中文"],
  ["en", "英语"],
  ["ja", "日语"],
  ["ko", "韩语"],
  ["ru", "俄语"],
  ["fr", "法语"],
  ["es", "西语"],
  ["de", "德语"],
  ["it", "意语"],
  ["ar", "阿语"],
];
interface QASource { index: number; similarity: number; type: string; content: string; materialName: string }

interface SceneVideo {
  id: string;
  title: string;
  sceneId: string;
  sceneName: string;
  category: string;
  sizeBytes: number;
  durationSec: number;
  status: string;
  playCount: number;
  createdAt: string;
  mediaUrl: string;    // 非空表示来自讲解音频段（可走公网 URL 直接播放）
  usageCount: number;  // 被多少段讲解音频引用
}

function SceneDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<"info" | "images" | "knowledge" | "audio" | "videos" | "qa">("info");
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<Scene | null>(null);
  const [images, setImages] = useState<SceneImage[]>([]);
  const [knowledge, setKnowledge] = useState<SceneKnowledge[]>([]);
  const [materials, setMaterials] = useState<SceneMaterial[]>([]);
  const [audio, setAudio] = useState<SceneAudio>({ script: "", voice: "鲟侠船长", audioUrl: "", duration: "", status: "none" });
  const [videos, setVideos] = useState<SceneVideo[]>([]);
  const [showLinkVideo, setShowLinkVideo] = useState(false);
  const [showUploadVideo, setShowUploadVideo] = useState(false);
  const [busy, setBusy] = useState<string>("");

  const imgInput = useRef<HTMLInputElement>(null);
  const docInput = useRef<HTMLInputElement>(null);

  const [tracks, setTracks] = useState<AudioTrack[]>([]);
  const [playingTrackId, setPlayingTrackId] = useState<string>("");
  const trackPlayerRef = useRef<HTMLAudioElement | null>(null);

  // All editing state lives above any conditional return (hooks order).
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({
    name: "", description: "", recognitionHint: "", tags: "",
    lat: 0, lng: 0, triggerRadius: 300, visibility: "public", voice: "鲟侠船长", status: "active",
  });
  const [kTitle, setKTitle] = useState(""); const [kContent, setKContent] = useState(""); const [adding, setAdding] = useState(false);
  const [kPurpose, setKPurpose] = useState<"main" | "faq">("main");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [purposeFilter, setPurposeFilter] = useState<"all" | "main" | "faq">("all");
  // Track voices come from the personas config (系统配置 · 三人格); the
  // hardcoded pair is only the pre-fetch fallback.
  const [voices, setVoices] = useState<string[]>(TRACK_VOICES);
  useEffect(() => {
    apiGet<{ name: string; enabled: boolean }[]>("/personas-config")
      .then((ps) => {
        const names = ps.filter((x) => x.enabled).map((x) => x.name);
        if (names.length) setVoices(names);
      })
      .catch(() => {});
  }, []);
  const [selVoices, setSelVoices] = useState<string[]>([TRACK_VOICES[0]]);
  const [selLangs, setSelLangs] = useState<string[]>(["zh"]);
  const [question, setQuestion] = useState(""); const [answer, setAnswer] = useState<{ answer: string; sources: QASource[] } | null>(null);

  // 图片/视频"替换"流程的 hooks 必须在任何 early return 之前 —— 否则在
  // 第一次 loading 渲染只跑了 N 个 hook、第二次跑了 N+4 个，React 会 throw
  // "Rendered more hooks than during the previous render" 让整页白屏。
  const replaceImgInput = useRef<HTMLInputElement>(null);
  const [replaceImgId, setReplaceImgId] = useState<string>("");
  const replaceVidInput = useRef<HTMLInputElement>(null);
  const [replaceVidId, setReplaceVidId] = useState<string>("");

  const stopTrackPlayback = () => {
    trackPlayerRef.current?.pause();
    trackPlayerRef.current = null;
    setPlayingTrackId("");
  };

  const onToggleTrack = (t: AudioTrack) => {
    if (playingTrackId === t.id) {
      stopTrackPlayback();
      return;
    }
    trackPlayerRef.current?.pause();
    const player = new Audio(t.audioUrl);
    trackPlayerRef.current = player;
    player.onended = () => stopTrackPlayback();
    player.onerror = () => { stopTrackPlayback(); toast("音频加载失败", "error"); };
    player.play().catch(() => stopTrackPlayback());
    setPlayingTrackId(t.id);
  };

  // Stop playback when navigating to another scene or leaving the page.
  useEffect(() => stopTrackPlayback, [id]);

  const reloadAll = async () => {
    if (!id) return;
    try {
      const [im, kn, mat, au, tr, sc, vd] = await Promise.all([
        apiGet<SceneImage[]>(`/scenes/${id}/images`),
        apiGet<SceneKnowledge[]>(`/scenes/${id}/knowledge`),
        apiGet<SceneMaterial[]>(`/scenes/${id}/materials`),
        apiGet<SceneAudio>(`/scenes/${id}/audio`),
        apiGet<AudioTrack[]>(`/scenes/${id}/audio/tracks`),
        apiGet<Scene>(`/scenes/${id}`),
        apiGet<SceneVideo[]>(`/scenes/${id}/videos`).catch(() => [] as SceneVideo[]),
      ]);
      setImages(im || []); setKnowledge(kn || []); setMaterials(mat || []);
      if (au) setAudio(au);
      setTracks(tr || []);
      if (sc) setInfo(sc);
      setVideos(vd || []);
    } catch { /* keep current */ } finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); setInfo(null); reloadAll(); /* eslint-disable-next-line */ }, [id]);
  // Poll while any material is parsing or any audio track is generating.
  useEffect(() => {
    const pending =
      materials.some((m) => m.parseStatus === "pending" || m.parseStatus === "parsing") ||
      tracks.some((t) => t.status === "generating");
    if (!pending) return;
    const t = setInterval(reloadAll, 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [materials, tracks]);

  if (loading && !info) {
    return <div className="m-6 bg-white border border-gray-100 rounded-xl p-12 text-center text-sm text-gray-400">加载中…</div>;
  }
  if (!info || !id) {
    return (
      <div className="m-6 bg-white border border-gray-100 rounded-xl p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
          <i className="ri-error-warning-line text-gray-300 text-2xl" />
        </div>
        <p className="text-sm text-gray-500 mb-4">景点不存在或已删除</p>
        <Link to="/knowledge/scenes" className="text-sm text-teal-600 hover:text-teal-700">返回景点列表</Link>
      </div>
    );
  }

  const cur = info;
  // PUT replaces all columns: spread the freshest row, then the patch.
  const saveScene = async (patch: Partial<Scene>) => {
    await apiSend("PUT", `/scenes/${id}`, { ...cur, ...patch });
    await reloadAll();
  };

  const startEditInfo = () => {
    setInfoForm({
      name: cur.name, description: cur.description, recognitionHint: cur.recognitionHint ?? "",
      tags: (cur.tags ?? []).join("，"),
      lat: cur.lat, lng: cur.lng, triggerRadius: cur.triggerRadius,
      visibility: cur.visibility, voice: cur.voice, status: cur.status,
    });
    setEditingInfo(true);
  };
  const onSaveInfo = async () => {
    if (!infoForm.name.trim()) { toast("名称不能为空", "info"); return; }
    setBusy("保存基本信息…");
    try {
      await saveScene({
        name: infoForm.name.trim(),
        description: infoForm.description.trim(),
        recognitionHint: infoForm.recognitionHint.trim(),
        tags: infoForm.tags.split(/[,，、\s]+/).map((t) => t.trim()).filter(Boolean),
        lat: Number(infoForm.lat), lng: Number(infoForm.lng),
        triggerRadius: Number(infoForm.triggerRadius) || 300,
        visibility: infoForm.visibility as Scene["visibility"],
        voice: infoForm.voice,
        status: infoForm.status as Scene["status"],
      });
      setEditingInfo(false);
      toast("已保存", "success");
    } catch (e) { toast("保存失败：" + (e as Error).message, "error"); } finally { setBusy(""); }
  };

  const onSetCover = async (url: string) => {
    setBusy("设置封面…");
    try { await saveScene({ coverUrl: url }); toast("封面已更新", "success"); }
    catch (e) { toast("设置失败：" + (e as Error).message, "error"); } finally { setBusy(""); }
  };

  // ---- Images ----
  // 图片资源现在统一从图片 tab 上传，讲解音频 tab 改成"下拉关联现有图片"，
  // 避免同一张图被多个段反复上传。
  const onUploadImage = async (f: File) => {
    setBusy("上传图片中…");
    try { await apiUpload(`/scenes/${id}/images`, f); await reloadAll(); }
    catch (e) { toast("上传失败：" + (e as Error).message, "error"); } finally { setBusy(""); }
  };
  const onDescribe = async (iid: string) => {
    setBusy("AI 分析图片中…");
    try { await apiSend("POST", `/images/${iid}/describe`); await reloadAll(); }
    catch (e) { toast("AI 描述失败：" + (e as Error).message, "error"); } finally { setBusy(""); }
  };
  const onDeleteImage = async (img: SceneImage) => {
    const msg = img.usageCount > 0
      ? `该图片正被本点位的 ${img.usageCount} 段讲解音频引用，删除后会一并把这些段的媒体清空（讲解词不影响）。\n确认删除？`
      : "确认删除该图片？删除后无法恢复。";
    if (!await confirmDialog({ title: "删除图片", message: msg, confirmText: "删除" })) return;
    await apiSend("DELETE", `/images/${img.id}`);
    toast(img.usageCount > 0 ? `已删除并同步清空 ${img.usageCount} 段媒体` : "已删除", "success");
    await reloadAll();
  };
  const onReplaceImage = async (f: File) => {
    if (!replaceImgId) return;
    setBusy("替换图片中…");
    try {
      const res = await apiUpload<{ syncedSegments: number }>(`/images/${replaceImgId}/replace`, f);
      toast(`已替换${res?.syncedSegments ? `，同步更新 ${res.syncedSegments} 段讲解` : ""}`, "success");
      await reloadAll();
    } catch (e) {
      toast("替换失败：" + (e as Error).message, "error");
    } finally { setBusy(""); setReplaceImgId(""); }
  };

  // ---- Videos (统一在本 tab 替换/删除；上传只在讲解音频里) ----
  const onDeleteVideo = async (v: SceneVideo) => {
    const msg = v.usageCount > 0
      ? `该视频正被本点位的 ${v.usageCount} 段讲解音频引用，删除后会一并把这些段的媒体清空。\n确认删除？`
      : "确认删除该视频？删除后无法恢复。";
    if (!await confirmDialog({ title: "删除视频", message: msg, confirmText: "删除" })) return;
    try {
      await apiSend("DELETE", `/videos/${v.id}`);
      toast(v.usageCount > 0 ? `已删除并同步清空 ${v.usageCount} 段媒体` : "已删除", "success");
      await reloadAll();
    } catch (e) {
      toast("删除失败：" + (e as Error).message, "error");
    }
  };
  const onReplaceVideo = async (f: File) => {
    if (!replaceVidId) return;
    setBusy("替换视频中…（大文件需等待上传完成）");
    try {
      const res = await apiUpload<{ syncedSegments: number }>(`/videos/${replaceVidId}/replace`, f);
      toast(`已替换${res?.syncedSegments ? `，同步更新 ${res.syncedSegments} 段讲解` : ""}`, "success");
      await reloadAll();
    } catch (e) {
      toast("替换失败：" + (e as Error).message, "error");
    } finally { setBusy(""); setReplaceVidId(""); }
  };

  // ---- Knowledge ----
  const onAddKnowledge = async () => {
    if (!kContent.trim()) return;
    if (kPurpose === "faq" && !kTitle.trim()) { toast("FAQ 需要填写问题", "info"); return; }
    // FAQ is stored as zh text only — never fed into audio generation.
    const content = kPurpose === "faq" ? `问：${kTitle.trim()}\n答：${kContent.trim()}` : kContent;
    setBusy("保存并向量化…");
    try { await apiSend("POST", `/scenes/${id}/knowledge`, { title: kTitle, content, purpose: kPurpose }); setKTitle(""); setKContent(""); setAdding(false); await reloadAll(); }
    finally { setBusy(""); }
  };
  const onToggleKnowledge = async (kid: string) => { await apiSend("POST", `/knowledge/${kid}/toggle`); await reloadAll(); };
  const onDeleteKnowledge = async (kid: string) => { await apiSend("DELETE", `/knowledge/${kid}`); await reloadAll(); };
  const onUploadDoc = async (f: File) => {
    setBusy("上传并解析入库中…");
    try { await apiUpload(`/scenes/${id}/materials`, f); await reloadAll(); } finally { setBusy(""); }
  };

  const startEdit = (item: SceneKnowledge) => { setEditingId(item.id); setEditTitle(item.title); setEditContent(item.content); };
  const onSaveEdit = async () => {
    if (!editingId || !editContent.trim()) return;
    setBusy("保存并重新向量化…");
    try { await apiSend("PUT", `/knowledge/${editingId}`, { title: editTitle, content: editContent }); setEditingId(null); await reloadAll(); }
    catch (e) { toast("保存失败：" + (e as Error).message, "error"); } finally { setBusy(""); }
  };

  // AI FAQ generation from the scene's whole enabled knowledge (exguide logic).
  const onGenerateFaq = async () => {
    if (!(await confirmDialog({ title: "AI 生成 FAQ", message: "将基于本景点全部已启用的知识内容自动生成常见问答（覆盖原有 AI 生成的 FAQ，人工 FAQ 保留），可能需要 1-2 分钟。", confirmText: "开始生成", danger: false }))) return;
    setBusy("AI 生成 FAQ 中（约1-2分钟）…");
    try {
      const r = await apiSend<{ created: number }>("POST", `/scenes/${id}/knowledge/generate-faq`);
      toast(`已生成 ${r.created} 条 FAQ`, "success");
      await reloadAll();
    } catch (e) { toast("FAQ 生成失败：" + (e as Error).message, "error"); } finally { setBusy(""); }
  };

  const onReparseMaterial = async (mid: string) => {
    setBusy("重新解析中…");
    try { await apiSend("POST", `/materials/${mid}/reparse`); await reloadAll(); } finally { setBusy(""); }
  };
  const onDeleteMaterial = async (mid: string) => {
    if (!(await confirmDialog({ title: "删除素材", message: "删除该素材将一并删除其生成的知识块。", confirmText: "删除" }))) return;
    await apiSend("DELETE", `/materials/${mid}`); await reloadAll();
  };

  const visibleKnowledge = knowledge.filter((k) =>
    purposeFilter === "all" ? true : purposeFilter === "faq" ? k.purpose === "faq" : k.purpose !== "faq",
  );

  // ---- Audio ----
  const onSaveScript = async () => { await apiSend("PUT", `/scenes/${id}/audio`, { script: audio.script, voice: audio.voice }); await reloadAll(); };
  const onPreview = async () => {
    if (!audio.script.trim()) return;
    setBusy("试听合成中…");
    try {
      const res = await apiFetch(`/tts/preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: audio.script, voice: audio.voice }) });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob(); new Audio(URL.createObjectURL(blob)).play();
    } catch (e) { toast("试听失败：" + (e as Error).message, "error"); } finally { setBusy(""); }
  };
  const onGenerate = async () => {
    setBusy("生成并缓存音频中…");
    try { await apiSend("PUT", `/scenes/${id}/audio`, { script: audio.script, voice: audio.voice }); await apiSend("POST", `/scenes/${id}/audio/generate`); await reloadAll(); }
    catch (e) { toast("生成失败：" + (e as Error).message, "error"); } finally { setBusy(""); }
  };

  // ---- Multi-role / multi-language tracks ----
  const toggleIn = (list: string[], v: string) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const onGenerateTracks = async () => {
    if (selVoices.length === 0 || selLangs.length === 0) {
      toast("请至少选择一个角色和一种语言", "info");
      return;
    }
    if (!audio.script.trim()) {
      toast("讲解词为空，请先填写并保存", "info");
      return;
    }
    try {
      await apiSend("PUT", `/scenes/${id}/audio`, { script: audio.script, voice: audio.voice });
      await apiSend("POST", `/scenes/${id}/audio/tracks/generate`, { voices: selVoices, langs: selLangs });
      await reloadAll(); // polling kicks in while generating
    } catch (e) { toast("生成请求失败：" + (e as Error).message, "error"); }
  };

  const trackOf = (voice: string, lang: string) => tracks.find((t) => t.voice === voice && t.lang === lang);
  const onDeleteTrack = async (t: AudioTrack) => {
    if (!(await confirmDialog({ title: "删除音频", message: `删除 ${t.voice} / ${TRACK_LANGS.find(([c]) => c === t.lang)?.[1]} 的音频？`, confirmText: "删除" }))) return;
    await apiSend("DELETE", `/audio/tracks/${t.id}`); await reloadAll();
  };

  // ---- QA ----
  const onAsk = async () => {
    if (!question.trim()) return;
    setBusy("检索并回答中…"); setAnswer(null);
    try { const r = await apiSend<{ answer: string; sources: QASource[] }>("POST", `/scenes/${id}/qa`, { question }); setAnswer(r); }
    catch (e) { toast("问答失败：" + (e as Error).message, "error"); } finally { setBusy(""); }
  };

  const tabs = [
    { key: "info" as const, label: "基本信息", count: null },
    { key: "images" as const, label: "图片", count: images.length },
    { key: "audio" as const, label: "讲解音频", count: null },
    { key: "videos" as const, label: "视频", count: videos.length },
  ];

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
        <Link to="/knowledge/scenes" className="hover:text-teal-600">景点导览内容</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-gray-700 font-medium">{cur.name}</span>
        {busy && <span className="ml-3 text-xs text-teal-600 animate-pulse">{busy}</span>}
      </div>

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        {cur.coverUrl ? (
          <img src={cur.coverUrl} alt={cur.name} className="w-16 h-16 rounded-lg object-cover bg-gray-100 shrink-0" />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
            <i className="ri-image-line text-gray-300 text-xl" />
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-gray-900 truncate">{cur.name}</h1>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${cur.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>
              {cur.status === "active" ? "已激活" : "未激活"}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            编号 {cur.seq ?? "-"} · <span className="font-mono">{cur.lat}, {cur.lng}</span> · 触发半径 {cur.triggerRadius}m
          </p>
        </div>
      </div>

      {/* Pill tabs (exguide style) */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${activeTab === tab.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            {tab.label}
            {tab.count !== null && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? "bg-teal-50 text-teal-600" : "bg-gray-200 text-gray-500"}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-100 rounded-xl p-6">
        {activeTab === "info" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900">基本信息</span>
              {!editingInfo && (
                <button onClick={startEditInfo} className="px-3 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50">
                  <i className="ri-edit-line mr-1" />编辑
                </button>
              )}
            </div>

            {!editingInfo ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                  <div><span className="text-xs text-gray-500 block mb-1">景点名称</span><span className="text-sm font-medium text-gray-900">{cur.name}</span></div>
                  <div><span className="text-xs text-gray-500 block mb-1">编号</span><span className="text-sm font-medium text-gray-900 font-mono">{cur.seq ?? "-"}</span></div>
                  <div><span className="text-xs text-gray-500 block mb-1">默认讲解音色</span><span className="text-sm font-medium text-gray-900">{cur.voice || "-"}</span></div>
                  <div className="col-span-2 lg:col-span-3"><span className="text-xs text-gray-500 block mb-1">简介</span><span className="text-sm text-gray-700 whitespace-pre-wrap">{cur.description || <span className="text-gray-400">未填写</span>}</span></div>
                  <div className="col-span-2 lg:col-span-3">
                    <span className="text-xs text-gray-500 block mb-1">识别特征 <span className="text-gray-400">用于视觉识别区分近似场景</span></span>
                    <span className="text-sm text-gray-700 whitespace-pre-wrap">{cur.recognitionHint || <span className="text-gray-400">未填写</span>}</span>
                  </div>
                  <div><span className="text-xs text-gray-500 block mb-1">位置</span><span className="text-sm font-medium text-gray-900 font-mono">{cur.lat}, {cur.lng}</span></div>
                  <div><span className="text-xs text-gray-500 block mb-1">触发半径</span><span className="text-sm font-medium text-gray-900">{cur.triggerRadius}m</span></div>
                  <div><span className="text-xs text-gray-500 block mb-1">可见性</span><span className="text-sm font-medium text-gray-900">{cur.visibility === "public" ? "公开" : "指定"}</span></div>
                  <div>
                    <span className="text-xs text-gray-500 block mb-1">标签</span>
                    <span className="flex flex-wrap gap-1.5">
                      {(cur.tags ?? []).length > 0 ? (cur.tags ?? []).map((t) => (
                        <span key={t} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">{t}</span>
                      )) : <span className="text-sm text-gray-400">无</span>}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block mb-1.5">封面图 <span className="text-gray-400">在「图片」标签页中点「设为封面」更换</span></span>
                  {cur.coverUrl ? (
                    <img src={cur.coverUrl} alt="封面" className="w-36 h-28 object-cover rounded-lg border border-gray-200" />
                  ) : (
                    <div className="w-36 h-28 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-gray-300"><i className="ri-image-line text-2xl" /></div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3 max-w-3xl">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs text-gray-500 mb-1">景点名称 *</label><input value={infoForm.name} onChange={(e) => setInfoForm({ ...infoForm, name: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">默认讲解音色</label>
                    <select value={infoForm.voice} onChange={(e) => setInfoForm({ ...infoForm, voice: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                      {voices.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div><label className="block text-xs text-gray-500 mb-1">简介</label><textarea value={infoForm.description} onChange={(e) => setInfoForm({ ...infoForm, description: e.target.value })} className="w-full h-20 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" /></div>
                <div><label className="block text-xs text-gray-500 mb-1">识别特征（用于视觉识别区分近似场景）</label><textarea value={infoForm.recognitionHint} onChange={(e) => setInfoForm({ ...infoForm, recognitionHint: e.target.value })} placeholder="如：坝顶有白色泄洪闸门编号牌；观景台栏杆为蓝色…" className="w-full h-20 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" /></div>
                <div><label className="block text-xs text-gray-500 mb-1">标签（逗号分隔）</label><input value={infoForm.tags} onChange={(e) => setInfoForm({ ...infoForm, tags: e.target.value })} placeholder="大坝，工程，必看" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs text-gray-500 mb-1">纬度</label><input type="number" step="0.0001" value={infoForm.lat} onChange={(e) => setInfoForm({ ...infoForm, lat: Number(e.target.value) })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">经度</label><input type="number" step="0.0001" value={infoForm.lng} onChange={(e) => setInfoForm({ ...infoForm, lng: Number(e.target.value) })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">触发半径（米）</label><input type="number" min={50} step={50} value={infoForm.triggerRadius} onChange={(e) => setInfoForm({ ...infoForm, triggerRadius: Number(e.target.value) })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">可见性 / 状态</label>
                    <div className="flex gap-2">
                      <select value={infoForm.visibility} onChange={(e) => setInfoForm({ ...infoForm, visibility: e.target.value })} className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-sm"><option value="public">公开</option><option value="private">指定</option></select>
                      <select value={infoForm.status} onChange={(e) => setInfoForm({ ...infoForm, status: e.target.value })} className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-sm"><option value="active">已激活</option><option value="inactive">未激活</option></select>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={onSaveInfo} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">保存</button>
                  <button onClick={() => setEditingInfo(false)} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">取消</button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "images" && (
          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">所有讲解段的段前图片都从这里上传，讲解音频 tab 直接下拉关联使用，同一张图不再重传。</p>
              <button
                onClick={() => imgInput.current?.click()}
                className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"
              >
                <i className="ri-upload-2-line mr-1" />上传图片
              </button>
            </div>
            <input
              ref={imgInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadImage(f); e.target.value = ""; }}
            />
            <input
              ref={replaceImgInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onReplaceImage(f); e.target.value = ""; }}
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {images.map((img) => (
                <div key={img.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="w-full h-32 bg-gray-100 overflow-hidden relative">
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                    {img.usageCount > 0 && (
                      <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-teal-600 text-white text-[10px] font-semibold">
                        讲解引用 {img.usageCount}
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-gray-500 mb-2 line-clamp-3">{img.description || <span className="text-amber-600">待分析</span>}</p>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => { setReplaceImgId(img.id); replaceImgInput.current?.click(); }}
                        className="text-xs text-teal-600 hover:text-teal-700"
                      >
                        <i className="ri-refresh-line mr-0.5" />替换
                      </button>
                      <button onClick={() => onDescribe(img.id)} className="text-xs text-gray-500 hover:text-teal-700">AI生成描述</button>
                      {cur.coverUrl === img.url ? (
                        <span className="text-xs text-amber-600"><i className="ri-star-fill mr-0.5" />封面</span>
                      ) : (
                        <button onClick={() => onSetCover(img.url)} className="text-xs text-gray-500 hover:text-amber-600">设为封面</button>
                      )}
                      <button onClick={() => onDeleteImage(img)} className="text-xs text-red-500 hover:text-red-600">删除</button>
                    </div>
                  </div>
                </div>
              ))}
              {images.length === 0 && <p className="col-span-full text-sm text-gray-400 text-center py-8">暂无图片，请到「讲解音频」tab 在对应段落里上传图片素材</p>}
            </div>
          </div>
        )}

        {false && activeTab === "knowledge" && (
          <div className="space-y-3">
            <input ref={docInput} type="file" accept=".doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.txt" className="hidden" onChange={(e) => e.target.files?.[0] && onUploadDoc(e.target.files[0])} />
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setAdding((v) => !v)} className="px-3 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-100"><i className="ri-add-line mr-1" />添加知识块</button>
              <button onClick={() => docInput.current?.click()} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"><i className="ri-file-upload-line mr-1" />上传文档解析入库</button>
              <button onClick={onGenerateFaq} disabled={!!busy} className="px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-100 disabled:opacity-50"><i className="ri-magic-line mr-1" />AI生成FAQ</button>
              <div className="ml-auto flex items-center bg-gray-100 rounded-lg p-0.5">
                {([["all", "全部"], ["main", "讲解"], ["faq", "FAQ"]] as const).map(([v, label]) => (
                  <button key={v} onClick={() => setPurposeFilter(v)} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${purposeFilter === v ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {adding && (
              <div className="border border-teal-200 rounded-lg p-3 space-y-2 bg-teal-50/40">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500">类别：</span>
                  {([["main", "主讲解内容"], ["faq", "FAQ（仅中文文字，不生成语音）"]] as const).map(([v, label]) => (
                    <button key={v} onClick={() => setKPurpose(v)} className={`px-2.5 py-1.5 rounded-full font-medium transition-colors ${kPurpose === v ? "bg-teal-600 text-white" : "bg-white border border-gray-200 text-gray-600"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <input value={kTitle} onChange={(e) => setKTitle(e.target.value)} placeholder={kPurpose === "faq" ? "问题（必填），如：三峡大坝有多高？" : "标题（可选）"} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                <textarea value={kContent} onChange={(e) => setKContent(e.target.value)} placeholder={kPurpose === "faq" ? "答案（中文）…" : "知识内容…"} className="w-full h-24 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" />
                <button onClick={onAddKnowledge} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm">保存并向量化</button>
              </div>
            )}
            {materials.length > 0 && (
              <div className="text-xs text-gray-500 space-y-1">
                {materials.map((m) => (
                  <div key={m.id} className="flex items-center gap-2">
                    <i className="ri-file-text-line" /><span>{m.fileName}</span>
                    <span className={m.parseStatus === "parsed" ? "text-emerald-600" : m.parseStatus === "failed" ? "text-red-500" : "text-amber-600"}>
                      {m.parseStatus === "parsed" ? "已入库" : m.parseStatus === "failed" ? "解析失败" : "解析中…"}
                    </span>
                    {m.parseError && <span className="text-red-400 truncate max-w-48" title={m.parseError}>{m.parseError}</span>}
                    {m.parseStatus === "failed" && (
                      <button onClick={() => onReparseMaterial(m.id)} className="text-teal-600 hover:text-teal-700">重新解析</button>
                    )}
                    <button onClick={() => onDeleteMaterial(m.id)} className="text-red-400 hover:text-red-500">删除</button>
                  </div>
                ))}
              </div>
            )}
            {visibleKnowledge.map((item) => (
              <div key={item.id} className={`border rounded-lg p-4 ${item.isEnabled ? "border-gray-200" : "border-gray-100 bg-gray-50 opacity-70"}`}>
                <div className="flex items-center justify-between mb-2 gap-2">
                  <span className="text-sm font-medium text-gray-900 flex items-center gap-2 min-w-0">
                    <span className="truncate">{item.title || (item.type === "image" ? "图片描述" : "知识块")}</span>
                    {item.purpose === "faq" && (
                      <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 text-[10px] font-medium flex-shrink-0">FAQ</span>
                    )}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-gray-400">{item.source}</span>
                    <span className={`text-xs ${item.vectorStatus === "ready" ? "text-emerald-600" : "text-amber-600"}`}>{item.vectorStatus === "ready" ? "已向量化" : "待向量化"}</span>
                    <button onClick={() => startEdit(item)} className="text-xs text-gray-500 hover:text-teal-600">编辑</button>
                    <button onClick={() => onToggleKnowledge(item.id)} className="text-xs text-gray-500 hover:text-teal-600">{item.isEnabled ? "停用" : "启用"}</button>
                    <button onClick={() => onDeleteKnowledge(item.id)} className="text-xs text-red-500 hover:text-red-600">删除</button>
                  </div>
                </div>
                {editingId === item.id ? (
                  <div className="space-y-2">
                    <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="标题（可选）" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                    <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} className="w-full h-28 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" />
                    <div className="flex gap-2">
                      <button onClick={onSaveEdit} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium">保存并重新向量化</button>
                      <button onClick={() => setEditingId(null)} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium">取消</button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.content}</p>
                )}
              </div>
            ))}
            {visibleKnowledge.length === 0 && <p className="text-sm text-gray-400 text-center py-8">暂无知识块</p>}
          </div>
        )}

        {activeTab === "audio" && (
          <SegmentsAudioPanel
            sceneId={id || ""}
            voices={voices}
            sceneImages={images}
            sceneVideos={videos}
            onSceneAssetsReload={reloadAll}
          />
        )}

        {/* 旧的单段 UI 暂时保留为 false 不渲染 —— 老 state / 函数仍在 hooks 链路中以避免破坏。 */}
        {false && activeTab === "audio" && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-900">讲解词</span>
                <span className="text-xs text-gray-400">{audio.script.length} 字</span>
              </div>
              <textarea value={audio.script} onChange={(e) => setAudio({ ...audio, script: e.target.value })} className="w-full h-32 p-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
            </div>
            <div className="flex items-center gap-3">
              <select value={audio.voice} onChange={(e) => setAudio({ ...audio, voice: e.target.value })} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
                {voices.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <button onClick={onSaveScript} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">保存</button>
              <button onClick={onPreview} className="px-4 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-100"><i className="ri-play-circle-line mr-1" />试听</button>
              <button onClick={onGenerate} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"><i className="ri-magic-line mr-1" />生成并缓存</button>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>状态：<span className={audio.status === "generated" ? "text-emerald-600" : "text-gray-500"}>{audio.status === "generated" ? "已生成" : audio.status === "pending" ? "待生成" : audio.status === "failed" ? "失败" : "未生成"}</span></span>
              {audio.audioUrl && <audio controls src={audio.audioUrl} className="h-8" />}
            </div>

            {/* Multi-role / multi-language tracks */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">多角色 · 多语言讲解音频</span>
                <span className="text-xs text-gray-400">音频存 OSS，App 端拉取后本地缓存</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="text-gray-500">角色：</span>
                {voices.map((v) => (
                  <button key={v} onClick={() => setSelVoices((s) => toggleIn(s, v))} className={`px-2.5 py-1.5 rounded-full font-medium transition-colors ${selVoices.includes(v) ? "bg-teal-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"}`}>
                    {v}
                  </button>
                ))}
                <span className="text-gray-500 ml-2">语言：</span>
                {TRACK_LANGS.map(([code, label]) => (
                  <button key={code} onClick={() => setSelLangs((s) => toggleIn(s, code))} className={`px-2.5 py-1.5 rounded-full font-medium transition-colors ${selLangs.includes(code) ? "bg-teal-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"}`}>
                    {label}
                  </button>
                ))}
                <span className="ml-auto flex items-center gap-2">
                  <button
                    onClick={() => { setSelVoices([...voices]); setSelLangs(TRACK_LANGS.map(([c]) => c)); }}
                    className="px-2.5 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-100"
                  >
                    全部选中
                  </button>
                  <button
                    onClick={() => { setSelVoices([]); setSelLangs([]); }}
                    className="px-2.5 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-100"
                  >
                    全部清除
                  </button>
                  <button onClick={onGenerateTracks} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700">
                    <i className="ri-magic-line mr-1" />批量生成所选
                  </button>
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-500 py-2 pr-3">角色 \ 语言</th>
                      {TRACK_LANGS.map(([code, label]) => (
                        <th key={code} className="text-left text-xs font-medium text-gray-500 py-2 pr-3">{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {voices.map((v) => (
                      <tr key={v} className="border-t border-gray-200">
                        <td className="py-2.5 pr-3 text-gray-700 font-medium whitespace-nowrap">{v}</td>
                        {TRACK_LANGS.map(([code]) => {
                          const t = trackOf(v, code);
                          return (
                            <td key={code} className="py-2.5 pr-3">
                              {!t || t.status === "pending" ? (
                                <span className="text-xs text-gray-400">未生成</span>
                              ) : t.status === "generating" ? (
                                <span className="text-xs text-amber-600"><i className="ri-loader-4-line animate-spin mr-0.5" />生成中</span>
                              ) : t.status === "failed" ? (
                                <span className="text-xs text-red-500" title={t.error}>失败</span>
                              ) : (
                                <span className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => onToggleTrack(t)}
                                    title={playingTrackId === t.id ? "停止" : "播放"}
                                    className={`w-6 h-6 flex items-center justify-center rounded-full text-white ${
                                      playingTrackId === t.id ? "bg-amber-500 hover:bg-amber-600" : "bg-teal-600 hover:bg-teal-700"
                                    }`}
                                  >
                                    <i className={`text-xs ${playingTrackId === t.id ? "ri-pause-fill" : "ri-play-fill"}`} />
                                  </button>
                                  <span className={`text-xs ${t.status === "stale" ? "text-amber-600" : "text-gray-500"}`}>
                                    {t.durationSec > 0 ? `${t.durationSec}s` : ""}
                                    {t.status === "stale" && " 已过期"}
                                  </span>
                                  <button onClick={() => onDeleteTrack(t)} title="删除" className="text-gray-300 hover:text-red-500">
                                    <i className="ri-close-line text-sm" />
                                  </button>
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400">讲解词修改保存后，已生成音频会标记「已过期」，重新批量生成即可覆盖；非中文语言先由 AI 翻译再合成。</p>
            </div>
          </div>
        )}

        {activeTab === "videos" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">所有讲解段的段前视频都从这里上传，讲解音频 tab 直接下拉关联使用，同一视频不再重传。</p>
              <button
                onClick={() => setShowUploadVideo(true)}
                className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 whitespace-nowrap"
              >
                <i className="ri-upload-line mr-1.5" />上传新视频
              </button>
            </div>
            <input
              ref={replaceVidInput}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onReplaceVideo(f); e.target.value = ""; }}
            />
            {videos.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-10 text-center">
                <i className="ri-film-line text-4xl text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">本点位还没有视频</p>
                <p className="text-xs text-gray-400 mt-1">点击右上"上传新视频"添加一个</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {videos.map((v) => {
                  const fromSegment = !!v.mediaUrl;
                  const previewUrl = fromSegment
                    ? (v.mediaUrl.startsWith("http") || v.mediaUrl.startsWith("/") ? v.mediaUrl : apiUrl(v.mediaUrl))
                    : "";
                  return (
                    <div key={v.id} className="border border-gray-200 rounded-lg p-4 hover:border-teal-300 hover:shadow-sm transition-all">
                      <div className="flex items-start justify-between mb-2 gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-900 truncate" title={v.title}>{v.title}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{v.category}</div>
                        </div>
                        {v.usageCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-teal-600 text-white text-[10px] font-semibold whitespace-nowrap">
                            讲解引用 {v.usageCount}
                          </span>
                        )}
                      </div>
                      {fromSegment && previewUrl && (
                        <video src={previewUrl} controls preload="metadata" className="w-full rounded mb-2 bg-black" />
                      )}
                      <div className="text-xs text-gray-500 space-y-0.5 mb-3">
                        <div>时长 {Math.floor(v.durationSec / 60)}:{String(v.durationSec % 60).padStart(2, "0")}</div>
                        <div>大小 {(v.sizeBytes / 1024 / 1024).toFixed(1)} MB</div>
                        <div className="text-gray-400">{v.createdAt}</div>
                      </div>
                      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                        {fromSegment ? (
                          <button
                            onClick={() => { setReplaceVidId(v.id); replaceVidInput.current?.click(); }}
                            className="flex-1 px-2 py-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded text-xs font-medium hover:bg-teal-100"
                          >
                            <i className="ri-refresh-line mr-1" />替换文件
                          </button>
                        ) : (
                          <Link
                            to="/videos"
                            className="flex-1 px-2 py-1.5 border border-gray-200 text-gray-700 rounded text-xs text-center hover:bg-gray-50"
                          >
                            <i className="ri-external-link-line mr-1" />在视频内容打开
                          </Link>
                        )}
                        <button
                          onClick={() => onDeleteVideo(v)}
                          className="px-2 py-1.5 border border-red-200 text-red-600 rounded text-xs font-medium hover:bg-red-50"
                          title="删除（同步清空所有引用此视频的讲解段）"
                        >
                          <i className="ri-delete-bin-line mr-1" />删除
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {false && activeTab === "qa" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onAsk()} placeholder="向本景点提问，例如：三峡大坝有多高？" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              <button onClick={onAsk} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">提问</button>
            </div>
            {answer && (
              <div className="space-y-3">
                <div className="bg-teal-50 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap">{answer.answer}</div>
                {answer.sources.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">命中知识块（{answer.sources.length}）</p>
                    <div className="space-y-2">
                      {answer.sources.map((s) => (
                        <div key={s.index} className="border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
                          <div className="flex justify-between mb-1"><span className="text-gray-400">{s.materialName || s.type}</span><span className="text-teal-600">相似度 {(s.similarity * 100).toFixed(0)}%</span></div>
                          <p className="line-clamp-3">{s.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showUploadVideo && (
        <UploadVideoModal
          sceneId={id || ""}
          onClose={() => setShowUploadVideo(false)}
          onUploaded={async () => { setShowUploadVideo(false); await reloadAll(); }}
        />
      )}
    </div>
  );
}

// 列出所有视频，让用户选一个关联到当前 scene；后端 PUT /videos/{vid}
// 改 sceneId 即可（接口已支持）。已绑定本 scene 的视频不再展示。
function LinkVideoModal({ sceneId, sceneName, onClose, onLinked }: {
  sceneId: string;
  sceneName: string;
  onClose: () => void;
  onLinked: () => void | Promise<void>;
}) {
  interface VideoRow {
    id: string; title: string; sceneId: string; sceneName: string;
    category: string; sizeBytes: number; durationSec: number; status: string;
    playCount: number; createdAt: string;
  }
  const [all, setAll] = useState<VideoRow[]>([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string>("");

  useEffect(() => { apiGet<VideoRow[]>("/videos").then(setAll).catch(() => setAll([])); }, []);

  const candidates = all
    .filter((v) => v.sceneId !== sceneId)
    .filter((v) => !q.trim() || v.title.includes(q) || v.sceneName.includes(q));

  const link = async (v: VideoRow) => {
    setBusy(v.id);
    try {
      await apiSend("PUT", `/videos/${v.id}`, { title: v.title, sceneId, category: v.category });
      toast(`视频「${v.title}」已关联到「${sceneName}」`, "success");
      await onLinked();
    } catch (e) {
      toast("关联失败: " + (e as Error).message, "error");
    } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-xl max-h-[90vh] flex flex-col">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h3 className="text-base font-semibold text-gray-900">关联已有视频</h3>
            <p className="text-xs text-gray-500 mt-0.5">从「视频内容」中选一个关联到「{sceneName}」</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <div className="px-6 pt-4">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜索视频标题或当前关联点位" className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {candidates.map((v) => (
            <div key={v.id} className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 hover:border-teal-300">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{v.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {v.sceneName ? <>当前已关联「{v.sceneName}」 · </> : <>未关联点位 · </>}
                  {v.category} · {(v.sizeBytes / 1024 / 1024).toFixed(1)} MB
                </div>
              </div>
              <button
                disabled={!!busy}
                onClick={() => link(v)}
                className="px-3 py-1.5 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700 disabled:opacity-60 whitespace-nowrap"
              >
                {busy === v.id ? "处理中…" : "关联到此点位"}
              </button>
            </div>
          ))}
          {candidates.length === 0 && (
            <div className="text-center py-8 text-sm text-gray-400">没有可关联的视频，请先到「视频内容」上传</div>
          )}
        </div>
      </div>
    </div>
  );
}

// 直接上传新视频到当前 scene；POST /videos multipart 带 sceneId。
// 上传后会自动出现在视频内容页 + 当前点位的视频 Tab。
function UploadVideoModal({ sceneId, onClose, onUploaded }: {
  sceneId: string;
  onClose: () => void;
  onUploaded: () => void | Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("景点视频");
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [busy, setBusy] = useState(false);

  // 让用户选完文件就尝试读时长（一些浏览器允许在 video 元素上拿 metadata）
  const onFile = (f: File | null) => {
    setFile(f);
    if (!f) { setDuration(0); return; }
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { setDuration(Math.round(v.duration || 0)); URL.revokeObjectURL(v.src); };
    v.src = URL.createObjectURL(f);
    if (!title.trim()) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async () => {
    if (!title.trim() || !file) { toast("标题和文件必填", "error"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("category", category.trim() || "景点视频");
      fd.append("sceneId", sceneId);
      fd.append("durationSec", String(duration || 0));
      fd.append("file", file);
      const res = await apiFetch("/videos", { method: "POST", body: fd, headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(await res.text().catch(() => `${res.status}`));
      toast("上传成功", "success");
      await onUploaded();
    } catch (e) {
      toast("上传失败: " + (e as Error).message, "error");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h3 className="text-base font-semibold text-gray-900">上传新视频</h3>
            <p className="text-xs text-gray-500 mt-0.5">上传后会同步出现在「视频内容」中</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">视频文件 <span className="text-red-500">*</span></label>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => onFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
            />
            {file && (
              <div className="text-xs text-gray-500 mt-1.5">
                {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
                {duration > 0 && <> · 时长 {Math.floor(duration / 60)}:{String(duration % 60).padStart(2, "0")}</>}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">标题 <span className="text-red-500">*</span></label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="例如：三峡大坝航拍" className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">分类</label>
            <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="景点视频 / 宣传片 / 纪录片 …" className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
          </div>
        </div>
        <div className="border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-2 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 whitespace-nowrap">取消</button>
          <button onClick={submit} disabled={busy || !file || !title.trim()} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60 whitespace-nowrap">
            {busy ? "上传中…" : "上传并关联本点位"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 4 段讲解词 + 分支（4-1 / 4-2）
// ============================================================

interface SegmentRow {
  // 无限扩展后 part 不再枚举：老 5 个槽位仍是 segment_1/segment_2/question/answer_yes/answer_no，
  // 新建段位走自由命名（c_xxxx / p_xxxx）。seq+role 决定渲染顺序与样式。
  part: string;
  variant?: SegmentVariant;
  seq: number;
  role: "content" | "prompt";
  script: string;
  pauseSec: number;
  voice: string;
  audioUrl: string;
  duration: string;
  status: "none" | "pending" | "generated" | "failed";
  // 段前可选媒体附件（图片或视频二选一），可空。讲解播报前展示。
  mediaUrl?: string;
  mediaType?: "image" | "video" | "";
}

// 3 套讲解风格 —— 简约 / 故事 / 话痨。每段都可独立录 3 套。
export type SegmentVariant = "concise" | "story" | "talky";

// 风格 → 锁定人格音色：与后端 presetVariantVoice 保持一致。
// 该 admin 页面里所有按 variant 渲染的 voice 列表都从这里过滤，避免用户勾选多余人格
// 触发额外翻译/合成（dashscope 计费）。
export const VARIANT_PERSONA_VOICE: Record<SegmentVariant, string> = {
  concise: "小船长",
  story:   "金梅导游",
  talky:   "鲟侠船长",
};

export const SEGMENT_VARIANTS: { key: SegmentVariant; label: string; hint: string }[] = [
  { key: "concise", label: "简约版", hint: "1-2 句点睛，3 套里最通用、推荐默认；历史已录内容归这套" },
  { key: "story",   label: "故事版", hint: "剧情向 6-8 句，适合喜欢听故事的游客" },
  { key: "talky",   label: "话痨版", hint: "深度展开 + 趣闻段子，适合研学 / 老客" },
];

// 单段定义。role=content 表示讲解段，role=prompt 表示中间的"可选 AI 互动问答"槽位（只录文案做标注，AI 决策逻辑不在这里）。
// label/hint 由 SegmentsAudioPanel 按段位顺序动态派生（不再绑定固定模板）。
interface SegmentDef {
  key: string;
  label: string;
  hint: string;
  role: "content" | "prompt";
  pauseLabel?: string;
}

// 中间问答提示文案 —— 所有 prompt 段共用同一段 hint。
const PROMPT_HINT_BETWEEN_SEGMENTS =
  '在这里写需要与游客互动的问题（可选）。问完之后需要接入 AI 理解游客的回应，决定要不要往下讲。此处只做文案标注，具体决策逻辑不在这里。';

const CONTENT_HINT_GENERIC =
  '点位的主体讲解内容；本风格下三套（简约/故事/话痨）互不覆盖。';

// 给新增段位生成稳定唯一的 part 名（[a-z0-9_]，validPart 限定 ≤64）。
// 旧 5 个 part（segment_1/segment_2/question/answer_yes/answer_no）保留兼容，
// 新建段位走 c_xxxxxx / p_xxxxxx 命名，方便日志一眼区分 role。
function newSegmentPart(role: "content" | "prompt"): string {
  const prefix = role === "prompt" ? "p" : "c";
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function SegmentsAudioPanel({
  sceneId, voices, sceneImages, sceneVideos, onSceneAssetsReload,
}: {
  sceneId: string;
  voices: string[];
  sceneImages: SceneImage[];
  sceneVideos: SceneVideo[];
  onSceneAssetsReload: () => Promise<void> | void;
}) {
  // 当前编辑的讲解风格 variant。每个 variant 下段位由后端按 seq 升序返回，
  // admin 可在任意位置追加 content / prompt 段位（DELETE 也走后端）。默认 concise —— 与生产历史数据归类一致。
  const [variant, setVariant] = useState<SegmentVariant>("concise");
  const [segments, setSegments] = useState<SegmentRow[]>([]);
  // 折叠状态按 `${variant}-${part}` 索引；新增的段位插入时默认展开（见 addSegment）。
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [addBusy, setAddBusy] = useState<"" | "content" | "prompt">("");

  const reload = async () => {
    if (!sceneId) return;
    try {
      const data = await apiGet<SegmentRow[]>(`/scenes/${sceneId}/segments?variant=${variant}`);
      setSegments(data || []);
    } catch { /* keep current */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [sceneId, variant]);

  // 按段位在 seq 升序中的位置动态派生 label/hint：content 段编号 1/2/3…，prompt 段编号 1/2…。
  const defs = useMemo<SegmentDef[]>(() => {
    let contentIdx = 0;
    let promptIdx = 0;
    return segments.map((row) => {
      if (row.role === "prompt") {
        promptIdx += 1;
        return {
          key: row.part,
          role: "prompt",
          label: `中间可选问答 ${promptIdx}`,
          hint: PROMPT_HINT_BETWEEN_SEGMENTS,
        };
      }
      contentIdx += 1;
      return {
        key: row.part,
        role: "content",
        label: `第 ${contentIdx} 段 · 内容 ${contentIdx}`,
        hint: CONTENT_HINT_GENERIC,
      };
    });
  }, [segments]);

  const flowText = useMemo(
    () => (defs.length === 0 ? "尚未配置任何段位，点击下方按钮新增。" : defs.map((d) => d.label).join(" → ")),
    [defs],
  );

  const currentHint = SEGMENT_VARIANTS.find((v) => v.key === variant)?.hint ?? "";

  const addSegment = async (role: "content" | "prompt") => {
    setAddBusy(role);
    try {
      const part = newSegmentPart(role);
      const maxSeq = segments.reduce((m, r) => Math.max(m, r.seq || 0), 0);
      await apiSend("PUT", `/scenes/${sceneId}/segments/${part}?variant=${variant}`, {
        script: "", pauseSec: 0, voice: "", role, seq: maxSeq + 10,
      });
      // 新建即展开，方便用户立刻录入内容
      setOpen((o) => ({ ...o, [`${variant}-${part}`]: true }));
      await reload();
    } catch (e) {
      toast("新增失败: " + (e as Error).message, "error");
    } finally { setAddBusy(""); }
  };

  const removeSegment = async (part: string, label: string) => {
    if (!(await confirmDialog({
      title: "删除段位",
      message: `确认删除「${label}」？该段位的讲解词与所有已生成音频（多角色 × 多语言）会一并清除，不可恢复。`,
      confirmText: "删除",
      danger: true,
    }))) return;
    try {
      await apiSend("DELETE", `/scenes/${sceneId}/segments/${part}?variant=${variant}`);
      toast("已删除", "success");
      await reload();
    } catch (e) { toast("删除失败: " + (e as Error).message, "error"); }
  };

  return (
    <div className="space-y-3">
      {/* 讲解风格切换 tabs —— 每个 variant 下段位 + 音轨完全独立 */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {SEGMENT_VARIANTS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => setVariant(v.key)}
            title={v.hint}
            className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition ${
              variant === v.key
                ? "border-teal-600 text-teal-700"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {v.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-gray-400 pb-2">{currentHint}</span>
      </div>

      <div className="bg-teal-50 border border-teal-200 rounded-lg px-4 py-3 text-xs text-teal-700 leading-relaxed">
        <i className="ri-information-line mr-1" />
        讲解流程：{flowText}。
        {' '}中间问答留空即视为不启用；填了之后需要接入 AI 理解游客回应再决定是否往下讲（具体逻辑不在这里录入）。
        当前编辑：<strong>{SEGMENT_VARIANTS.find(v=>v.key===variant)?.label}</strong>，三套互不覆盖；段位可按需新增 / 删除，按列表顺序播报。
      </div>

      {defs.map((def, i) => {
        const row = segments[i];
        const slotKey = `${variant}-${def.key}`;
        return (
          <SegmentCard
            key={slotKey}
            def={def}
            sceneId={sceneId}
            variant={variant}
            row={row}
            voices={voices}
            isOpen={open[slotKey] ?? def.role === "content"}
            onToggle={() => setOpen((o) => ({ ...o, [slotKey]: !(o[slotKey] ?? def.role === "content") }))}
            onReload={reload}
            onDelete={() => removeSegment(def.key, def.label)}
            sceneImages={sceneImages}
            sceneVideos={sceneVideos}
            onSceneAssetsReload={onSceneAssetsReload}
          />
        );
      })}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => addSegment("content")}
          disabled={!!addBusy}
          className="px-3 py-2 bg-white border border-gray-200 text-sm text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-60"
        >
          <i className="ri-add-line mr-1" />{addBusy === "content" ? "新增中…" : "新增内容段"}
        </button>
        <button
          type="button"
          onClick={() => addSegment("prompt")}
          disabled={!!addBusy}
          className="px-3 py-2 bg-white border border-blue-200 text-sm text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-60"
        >
          <i className="ri-question-answer-line mr-1" />{addBusy === "prompt" ? "新增中…" : "新增中间问答"}
        </button>
        <span className="text-xs text-gray-400 ml-2">新增的段位会追加到流程末尾</span>
      </div>
    </div>
  );
}

interface TrackRow {
  id: string;
  scene_id?: string; sceneId?: string;
  voice: string; lang: string;
  text: string; audioUrl: string; durationSec: number;
  status: string; error: string; updatedAt: string;
}

interface MediaAssetLite {
  id: string;
  type: "image" | "video";
  url: string;
  title: string;
  fileName: string;
  thumbUrl: string;
  durationSec: number;
  tags: string[];
  usageCount: number;
}

// 段前可选媒体:从全局媒体库挑一份挂到本段位。上传统一在「媒体库」页,这里只做
// 关联——同一张图/视频可被任意 scene 任意 segment 复用,不再到处重复上传。
// 点缩略图弹模态:搜索框 + 缩略图网格,比下拉好用。
function SegmentMediaPicker({
  sceneId, part, variant, mediaUrl, mediaType, onChange,
}: {
  sceneId: string;
  part: string;
  variant: SegmentVariant;
  mediaUrl: string;
  mediaType: "image" | "video" | "";
  onChange: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState<"" | "delete">("");
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleClear = async () => {
    setBusy("delete");
    try {
      await apiSend("DELETE", `/scenes/${sceneId}/segments/${part}/media?variant=${variant}`);
      toast("已清除段前媒体", "success");
      await onChange();
    } catch (e) {
      toast("清除失败: " + (e as Error).message, "error");
    } finally {
      setBusy("");
    }
  };

  const hasMedia = !!mediaUrl;
  const fullUrl = mediaUrl.startsWith("http") || mediaUrl.startsWith("/") ? mediaUrl : apiUrl(mediaUrl);

  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 px-3 py-2.5">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
        <i className="ri-attachment-2 text-gray-400" />
        <span>段前媒体附件（可选 · 从全局媒体库挑一份）</span>
        {hasMedia && (
          <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
            mediaType === "image" ? "bg-cyan-100 text-cyan-700" : "bg-fuchsia-100 text-fuchsia-700"
          }`}>
            {mediaType === "image" ? "图片" : mediaType === "video" ? "视频" : "媒体"}
          </span>
        )}
        <Link to="/knowledge/media-assets" className="ml-auto text-teal-600 hover:text-teal-700 text-[11px]">管理媒体库 →</Link>
      </div>

      <div className="flex items-start gap-3">
        {hasMedia ? (
          <div className="shrink-0 cursor-pointer" onClick={() => setPickerOpen(true)} title="点击替换">
            {mediaType === "video" ? (
              <video src={fullUrl} controls className="rounded border border-gray-200 max-h-32 max-w-[200px] bg-black" />
            ) : (
              <img src={fullUrl} alt="段前媒体" className="rounded border border-gray-200 max-h-32 max-w-[200px] object-contain bg-white" />
            )}
          </div>
        ) : (
          <button
            onClick={() => setPickerOpen(true)}
            className="flex-1 px-3 py-6 border border-dashed border-gray-300 text-sm text-gray-500 rounded hover:border-teal-400 hover:text-teal-700 bg-white"
          >
            <i className="ri-image-add-line mr-1" />从媒体库挑一份图片或视频
          </button>
        )}
        {hasMedia && (
          <div className="flex flex-col gap-1.5">
            <button onClick={() => setPickerOpen(true)} disabled={!!busy} className="px-2.5 py-1.5 bg-white border border-gray-200 text-xs text-gray-700 rounded hover:bg-gray-50 disabled:opacity-60">
              <i className="ri-image-2-line mr-1" />更换
            </button>
            <button onClick={handleClear} disabled={!!busy} className="px-2.5 py-1.5 bg-white border border-gray-200 text-xs text-red-600 rounded hover:bg-red-50 disabled:opacity-60">
              <i className="ri-close-circle-line mr-1" />{busy === "delete" ? "清除中…" : "清除"}
            </button>
          </div>
        )}
      </div>

      {pickerOpen && (
        <MediaAssetPickerModal
          sceneId={sceneId}
          part={part}
          variant={variant}
          currentUrl={mediaUrl}
          onClose={() => setPickerOpen(false)}
          onPicked={async () => { setPickerOpen(false); await onChange(); }}
        />
      )}
    </div>
  );
}

function MediaAssetPickerModal({
  sceneId, part, variant, currentUrl, onClose, onPicked,
}: {
  sceneId: string;
  part: string;
  variant: SegmentVariant;
  currentUrl: string;
  onClose: () => void;
  onPicked: () => Promise<void> | void;
}) {
  const [items, setItems] = useState<MediaAssetLite[]>([]);
  const [typeFilter, setTypeFilter] = useState<"" | "image" | "video">("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState<string>("");

  const load = async () => {
    setLoading(true);
    try {
      const params: string[] = [];
      if (typeFilter) params.push(`type=${typeFilter}`);
      if (q.trim()) params.push(`q=${encodeURIComponent(q.trim())}`);
      const list = await apiGet<MediaAssetLite[]>(`/media-assets${params.length ? "?" + params.join("&") : ""}`);
      setItems(list ?? []);
    } catch (e) {
      toast("加载媒体库失败：" + (e as Error).message, "error");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter]);

  const pick = async (a: MediaAssetLite) => {
    setPicking(a.id);
    try {
      await apiSend("PUT", `/scenes/${sceneId}/segments/${part}/media-link?variant=${variant}`, {
        url: a.url,
        type: a.type,
        durationSec: a.durationSec ?? 0,
      });
      toast("已挂载段前媒体", "success");
      await onPicked();
    } catch (e) {
      toast("挂载失败: " + (e as Error).message, "error");
    } finally { setPicking(""); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <h3 className="text-base font-bold text-gray-900">选择媒体</h3>
          <span className="text-xs text-gray-400">从全局媒体库挑一份图片或视频挂到本段</span>
          <button onClick={onClose} className="ml-auto w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            {([
              ["", "全部"],
              ["image", "图片"],
              ["video", "视频"],
            ] as const).map(([v, label]) => (
              <button
                key={v}
                onClick={() => setTypeFilter(v)}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${typeFilter === v ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              >{label}</button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="按 标题 / 描述 / 标签 搜索"
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
          <button onClick={load} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">搜索</button>
          <Link to="/knowledge/media-assets" target="_blank" className="px-3 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-100">
            <i className="ri-external-link-line mr-1" />去媒体库上传
          </Link>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <p className="text-center text-sm text-gray-400 py-8">加载中…</p>}
          {!loading && items.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-image-2-line text-4xl text-gray-300 block mb-3" />
              <p className="text-sm text-gray-500">没有匹配的媒体</p>
              <p className="text-xs text-gray-400 mt-1">先到 媒体库 上传，再回到这里选</p>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {items.map((a) => {
                const thumb = a.thumbUrl || (a.type === "image" ? a.url : "");
                const isCurrent = a.url === currentUrl;
                return (
                  <button
                    key={a.id}
                    onClick={() => !isCurrent && pick(a)}
                    disabled={!!picking || isCurrent}
                    className={`relative border rounded-lg overflow-hidden text-left transition-all ${
                      isCurrent ? "border-teal-500 ring-2 ring-teal-200" : "border-gray-200 hover:border-teal-400 hover:shadow"
                    } ${picking ? "opacity-60" : ""}`}
                  >
                    <div className="w-full h-28 bg-gray-100 relative">
                      {thumb ? (
                        <img src={thumb} alt={a.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <i className={a.type === "video" ? "ri-film-line text-3xl" : "ri-image-line text-3xl"} />
                        </div>
                      )}
                      <span className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                        a.type === "image" ? "bg-cyan-100 text-cyan-700" : "bg-fuchsia-100 text-fuchsia-700"
                      }`}>
                        {a.type === "image" ? "图" : `视频 ${a.durationSec > 0 ? `${Math.floor(a.durationSec / 60)}:${String(a.durationSec % 60).padStart(2, "0")}` : ""}`}
                      </span>
                      {isCurrent && (
                        <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-teal-600 text-white text-[10px] font-semibold">当前</span>
                      )}
                      {a.usageCount > 0 && !isCurrent && (
                        <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-gray-700/70 text-white text-[10px] font-semibold">引 {a.usageCount}</span>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-medium text-gray-900 truncate" title={a.title || a.fileName}>{a.title || a.fileName || "未命名"}</div>
                      {a.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {a.tags.slice(0, 2).map((t) => (
                            <span key={t} className="px-1 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px]">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SegmentCard({
  def, sceneId, variant, row, voices, isOpen, onToggle, onReload, onDelete,
  sceneImages, sceneVideos, onSceneAssetsReload,
}: {
  def: SegmentDef;
  sceneId: string;
  variant: SegmentVariant;
  row: SegmentRow;
  voices: string[];
  isOpen: boolean;
  onToggle: () => void;
  onReload: () => void | Promise<void>;
  onDelete: () => void | Promise<void>;
  sceneImages: SceneImage[];
  sceneVideos: SceneVideo[];
  onSceneAssetsReload: () => Promise<void> | void;
}) {
  const isPrompt = def.role === "prompt";
  // 按 variant 锁人格音色（与后端一致）；voices prop 里其它人格不显示，强制使用 personaVoice。
  const personaVoice = VARIANT_PERSONA_VOICE[variant];
  const voiceOptions = voices.includes(personaVoice) ? [personaVoice] : (voices[0] ? [voices[0]] : []);
  const [script, setScript] = useState(row.script);
  const [pauseSec, setPauseSec] = useState(row.pauseSec);
  const [voice, setVoice] = useState(personaVoice);
  const [busy, setBusy] = useState<string>("");
  // 当 row 变（reload 后）时把本地 state 也同步过去。voice 始终锁 personaVoice。
  useEffect(() => { setScript(row.script); setPauseSec(row.pauseSec); setVoice(personaVoice); }, [row.script, row.pauseSec, row.voice, personaVoice]);

  const enabled = script.trim().length > 0;
  const dirty = script !== row.script || pauseSec !== row.pauseSec || voice !== (row.voice || personaVoice);

  const save = async () => {
    setBusy("save");
    try {
      await apiSend("PUT", `/scenes/${sceneId}/segments/${def.key}?variant=${variant}`, { script, pauseSec, voice });
      toast("已保存", "success");
      await onReload();
    } catch (e) { toast("保存失败: " + (e as Error).message, "error"); } finally { setBusy(""); }
  };
  const preview = async () => {
    if (!enabled) { toast("讲解词为空", "error"); return; }
    setBusy("preview");
    try {
      const res = await apiSend<{ audioUrl: string }>("POST", "/tts/preview", { text: script, voice });
      const a = new Audio(res.audioUrl);
      a.play().catch(() => toast("无法播放", "error"));
    } catch (e) { toast("试听失败: " + (e as Error).message, "error"); } finally { setBusy(""); }
  };
  const generate = async () => {
    if (!enabled) { toast("讲解词为空", "error"); return; }
    if (dirty) { await save(); }
    setBusy("generate");
    try {
      await apiSend("POST", `/scenes/${sceneId}/segments/${def.key}/audio/generate?variant=${variant}`);
      toast("已生成", "success");
      await onReload();
    } catch (e) { toast("生成失败: " + (e as Error).message, "error"); } finally { setBusy(""); }
  };

  const statusLabel = !enabled ? "未启用"
    : row.status === "generated" ? "已生成"
    : row.status === "pending" ? "待生成"
    : row.status === "failed" ? "失败"
    : "未生成";
  const statusCls = !enabled ? "text-gray-400"
    : row.status === "generated" ? "text-emerald-600"
    : row.status === "failed" ? "text-red-600"
    : "text-amber-600";

  // Prompt 卡片用蓝色调突出"中间互动 + AI 决策"语义；main 卡片保持默认灰白。
  const cardBorderCls = isPrompt
    ? (enabled ? "border-blue-300 bg-blue-50/40" : "border-blue-200 border-dashed bg-blue-50/20")
    : (enabled ? "border-gray-200" : "border-gray-200 border-dashed");
  const hoverCls = isPrompt ? "hover:bg-blue-50" : "hover:bg-gray-50";

  return (
    <div className={`bg-white border rounded-lg ${cardBorderCls}`}>
      {/* Header */}
      <button onClick={onToggle} className={`w-full flex items-center justify-between px-4 py-3 ${hoverCls} rounded-t-lg`}>
        <div className="flex items-center gap-3 min-w-0">
          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
          {isPrompt && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-medium shrink-0">
              <i className="ri-question-answer-line" />可选 · AI 互动
            </span>
          )}
          <div className="text-left min-w-0">
            <div className={`text-sm font-semibold ${isPrompt ? "text-blue-900" : "text-gray-900"}`}>{def.label}</div>
            <div className="text-xs text-gray-400 truncate">{def.hint}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className={statusCls}>{statusLabel}</span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-400">{script.length} 字</span>
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-gray-100 p-4 space-y-3">
          <SegmentMediaPicker
            sceneId={sceneId}
            part={def.key}
            variant={variant}
            mediaUrl={row.mediaUrl || ""}
            mediaType={row.mediaType || ""}
            onChange={async () => { await onReload(); await onSceneAssetsReload(); }}
          />
          <textarea
            value={script}
            onChange={(e) => setScript(e.target.value)}
            placeholder={`输入${def.label.replace(/^第 ?[0-9-]+ 段 · ?/, "")}的内容；留空表示本段不启用`}
            className="w-full h-28 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
          <div className="flex items-center gap-3 flex-wrap">
            {def.pauseLabel && (
              <label className="flex items-center gap-2 text-xs text-gray-600">
                {def.pauseLabel}
                <input
                  type="number"
                  min={0}
                  max={300}
                  value={pauseSec}
                  onChange={(e) => setPauseSec(Number(e.target.value) || 0)}
                  className="w-20 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm"
                />
                <span className="text-gray-400">秒</span>
              </label>
            )}
            <select value={voice} onChange={(e) => setVoice(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" disabled={!enabled}>
              {voiceOptions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <button onClick={save} disabled={!!busy} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-60">
              {busy === "save" ? "保存中…" : "保存"}
            </button>
            <button onClick={preview} disabled={!!busy || !enabled} className="px-3 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-100 disabled:opacity-60">
              <i className="ri-play-circle-line mr-1" />{busy === "preview" ? "试听中…" : "试听"}
            </button>
            <button onClick={generate} disabled={!!busy || !enabled} className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60">
              <i className="ri-magic-line mr-1" />{busy === "generate" ? "生成中…" : "生成并缓存"}
            </button>
            {row.audioUrl && enabled && <audio controls src={row.audioUrl} className="h-8" />}
            <button onClick={onDelete} disabled={!!busy} className="ml-auto px-2.5 py-1.5 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded inline-flex items-center gap-1 disabled:opacity-60" title="删除此段位">
              <i className="ri-delete-bin-line" />删除此段位
            </button>
          </div>

          {/* 多角色 · 多语言矩阵（仅在本段启用后展开），按 variant 隔离 */}
          {enabled && (
            <SegmentTracksMatrix sceneId={sceneId} part={def.key} variant={variant} voices={voices} />
          )}
        </div>
      )}
    </div>
  );
}

function SegmentTracksMatrix({ sceneId, part, variant, voices }: { sceneId: string; part: string; variant: SegmentVariant; voices: string[] }) {
  // 风格锁定单一人格音色：concise=金梅 / story=鲟侠 / talky=江豚仔。
  // 即使 personas 启用了其他人格也不显示，防止误勾后触发多余 dashscope 翻译/合成。
  const personaVoice = VARIANT_PERSONA_VOICE[variant];
  const effectiveVoices = voices.includes(personaVoice) ? [personaVoice] : (voices[0] ? [voices[0]] : []);
  const [tracks, setTracks] = useState<TrackRow[]>([]);
  const [selVoices, setSelVoices] = useState<string[]>([effectiveVoices[0] || ""]);
  const [selLangs, setSelLangs] = useState<string[]>(["zh"]);
  const [playingTrackId, setPlayingTrackId] = useState<string>("");
  const playerRef = useRef<HTMLAudioElement | null>(null);

  const reload = async () => {
    if (!sceneId) return;
    try {
      const d = await apiGet<TrackRow[]>(`/scenes/${sceneId}/segments/${part}/audio/tracks?variant=${variant}`);
      setTracks(d || []);
    } catch { /* keep */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [sceneId, part, variant]);
  // 轮询：有 generating 时每 2.5s 拉一次
  useEffect(() => {
    if (!tracks.some((t) => t.status === "generating")) return;
    const t = setInterval(reload, 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [tracks]);

  const toggleIn = (list: string[], v: string) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const onGenerate = async () => {
    if (selVoices.length === 0 || selLangs.length === 0) { toast("请至少选择 1 个角色和 1 种语言", "info"); return; }
    try {
      await apiSend("POST", `/scenes/${sceneId}/segments/${part}/audio/tracks/generate?variant=${variant}`, { voices: selVoices, langs: selLangs });
      toast("已下发生成任务", "success");
      await reload();
    } catch (e) { toast("生成失败: " + (e as Error).message, "error"); }
  };
  const onToggleTrack = (t: TrackRow) => {
    if (playingTrackId === t.id) { playerRef.current?.pause(); playerRef.current = null; setPlayingTrackId(""); return; }
    playerRef.current?.pause();
    const p = new Audio(t.audioUrl);
    playerRef.current = p;
    p.onended = () => { playerRef.current = null; setPlayingTrackId(""); };
    p.onerror = () => { toast("加载失败", "error"); setPlayingTrackId(""); };
    p.play().catch(() => setPlayingTrackId(""));
    setPlayingTrackId(t.id);
  };
  const onDeleteTrack = async (t: TrackRow) => {
    await apiSend("DELETE", `/audio/tracks/${t.id}`);
    await reload();
  };
  const trackOf = (voice: string, lang: string) => tracks.find((t) => t.voice === voice && t.lang === lang);

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-900">多角色 · 多语言讲解音频</span>
        <span className="text-xs text-gray-400">音频存 OSS，App 端拉取后本地缓存</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-gray-500">角色：</span>
        {effectiveVoices.map((v) => (
          <button key={v} onClick={() => setSelVoices((s) => toggleIn(s, v))} className={`px-2.5 py-1.5 rounded-full font-medium transition-colors ${selVoices.includes(v) ? "bg-teal-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"}`}>
            {v}
          </button>
        ))}
        <span className="text-gray-500 ml-2">语言：</span>
        {TRACK_LANGS.map(([code, label]) => (
          <button key={code} onClick={() => setSelLangs((s) => toggleIn(s, code))} className={`px-2.5 py-1.5 rounded-full font-medium transition-colors ${selLangs.includes(code) ? "bg-teal-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"}`}>
            {label}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-2">
          <button onClick={() => { setSelVoices([...effectiveVoices]); setSelLangs(TRACK_LANGS.map(([c]) => c)); }} className="px-2.5 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-100">全部选中</button>
          <button onClick={() => { setSelVoices([]); setSelLangs([]); }} className="px-2.5 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-100">全部清除</button>
          <button onClick={onGenerate} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700"><i className="ri-magic-line mr-1" />批量生成所选</button>
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 py-2 pr-3">角色 \ 语言</th>
              {TRACK_LANGS.map(([code, label]) => (
                <th key={code} className="text-left text-xs font-medium text-gray-500 py-2 pr-3">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {effectiveVoices.map((v) => (
              <tr key={v} className="border-t border-gray-200">
                <td className="py-2.5 pr-3 text-gray-700 font-medium whitespace-nowrap">{v}</td>
                {TRACK_LANGS.map(([code]) => {
                  const t = trackOf(v, code);
                  return (
                    <td key={code} className="py-2.5 pr-3">
                      {!t || t.status === "pending" ? (
                        <span className="text-xs text-gray-400">未生成</span>
                      ) : t.status === "generating" ? (
                        <span className="text-xs text-amber-600"><i className="ri-loader-4-line animate-spin mr-0.5" />生成中</span>
                      ) : t.status === "failed" ? (
                        <span className="text-xs text-red-500" title={t.error}>失败</span>
                      ) : (
                        <span className="flex items-center gap-1.5">
                          <button onClick={() => onToggleTrack(t)} title={playingTrackId === t.id ? "停止" : "播放"} className={`w-6 h-6 flex items-center justify-center rounded-full text-white ${playingTrackId === t.id ? "bg-amber-500 hover:bg-amber-600" : "bg-teal-600 hover:bg-teal-700"}`}>
                            <i className={`text-xs ${playingTrackId === t.id ? "ri-pause-fill" : "ri-play-fill"}`} />
                          </button>
                          <span className={`text-xs ${t.status === "stale" ? "text-amber-600" : "text-gray-500"}`}>
                            {t.durationSec > 0 ? `${t.durationSec}s` : ""}
                            {t.status === "stale" && " 已过期"}
                          </span>
                          <button onClick={() => onDeleteTrack(t)} title="删除" className="text-gray-300 hover:text-red-500">
                            <i className="ri-close-line text-sm" />
                          </button>
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-400">讲解词修改保存后，已生成音频会标记「已过期」，重新批量生成即可覆盖；非中文语言先由 AI 翻译再合成。</p>
    </div>
  );
}

export default SceneDetailPage;
